import { useState, useEffect, useCallback } from "react";
import {
  Search, RefreshCw, X, Calendar, Factory, ClipboardList,
  CheckCircle2, Clock, PlayCircle, Download, Loader2, ArrowUpDown,
  ChevronUp, ChevronDown, Package
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";
import DataSourceNotice from "@/components/DataSourceNotice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  Created:              { color: "bg-slate-100 text-slate-700",   dot: "bg-slate-400" },
  Released:             { color: "bg-blue-100 text-blue-800",     dot: "bg-blue-500" },
  "Partially Confirmed":{ color: "bg-cyan-100 text-cyan-800",    dot: "bg-cyan-500" },
  Confirmed:            { color: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500" },
  "Partially Delivered":{ color: "bg-amber-100 text-amber-800",  dot: "bg-amber-500" },
  Delivered:            { color: "bg-green-100 text-green-800",   dot: "bg-green-600" },
  Completed:            { color: "bg-violet-100 text-violet-800", dot: "bg-violet-500" },
  Closed:               { color: "bg-zinc-200 text-zinc-700",    dot: "bg-zinc-500" },
  Deleted:              { color: "bg-red-100 text-red-700",       dot: "bg-red-500" },
};

const formatDate = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Created;
  return (
    <Badge className={`${cfg.color} hover:${cfg.color} gap-1.5`} data-testid={`status-badge-${status.toLowerCase().replace(/\s/g, "-")}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {status}
    </Badge>
  );
};

const ProductionOrderTracker = () => {
  const [orders, setOrders] = useState([]);
  const [kpis, setKpis] = useState({ total: 0, released: 0, in_progress: 0, completed: 0, created_not_released: 0 });
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dataSource, setDataSource] = useState("loading");
  const [sourceMessage, setSourceMessage] = useState("");

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Selection
  const [selected, setSelected] = useState(new Set());

  // Sort
  const [sortField, setSortField] = useState("po_number");
  const [sortDir, setSortDir] = useState("desc");

  // Release loading
  const [releasingSet, setReleasingSet] = useState(new Set());
  const [batchReleasing, setBatchReleasing] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/production-orders/tracker`);
      const data = resp.data;
      setOrders(data.orders || []);
      setKpis(data.kpis || {});
      setStatusCounts(data.status_counts || {});
      setDataSource(data.data_source || "sap_live");
      setSourceMessage("");
    } catch (err) {
      console.error("Failed to fetch PO tracker data:", err);
      toast.error("Failed to load production orders");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleRefresh = () => {
    setRefreshing(true);
    setSelected(new Set());
    fetchOrders();
    toast.success("Production orders refreshed");
  };

  // ---------- Filtering & Sorting ----------
  const filtered = orders.filter((o) => {
    if (statusFilter !== "all" && o.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        o.po_number.toLowerCase().includes(q) ||
        o.material.toLowerCase().includes(q) ||
        (o.sales_order && o.sales_order.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortField], bv = b[sortField];
    if (typeof av === "string") av = av.toLowerCase();
    if (typeof bv === "string") bv = bv.toLowerCase();
    if (av == null) return 1;
    if (bv == null) return -1;
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />;
  };

  // ---------- Selection ----------
  const releasable = sorted.filter((o) => o.status === "Created");
  const allReleasableSelected = releasable.length > 0 && releasable.every((o) => selected.has(o.po_number));

  const toggleSelect = (po) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(po) ? next.delete(po) : next.add(po);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allReleasableSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(releasable.map((o) => o.po_number)));
    }
  };

  // ---------- Release ----------
  const handleReleaseSingle = async (po_number) => {
    setReleasingSet((prev) => new Set(prev).add(po_number));
    try {
      const resp = await axios.post(`${API}/production-orders/release/${po_number}`);
      if (resp.data.success) {
        toast.success(`PO ${po_number} released`);
        fetchOrders();
      } else {
        toast.error(resp.data.message || "Release failed");
      }
    } catch (err) {
      toast.error(`Failed to release PO ${po_number}`);
    } finally {
      setReleasingSet((prev) => { const n = new Set(prev); n.delete(po_number); return n; });
    }
  };

  const handleBatchRelease = async () => {
    const poNumbers = [...selected].filter((po) => orders.find((o) => o.po_number === po && o.status === "Created"));
    if (poNumbers.length === 0) { toast.error("No releasable orders selected"); return; }
    setBatchReleasing(true);
    try {
      const resp = await axios.post(`${API}/production-orders/release-batch`, { po_numbers: poNumbers });
      const data = resp.data;
      toast.success(`Released ${data.success}/${data.total} orders`);
      if (data.failed > 0) toast.error(`${data.failed} orders failed to release`);
      setSelected(new Set());
      fetchOrders();
    } catch (err) {
      toast.error("Batch release failed");
    } finally {
      setBatchReleasing(false);
    }
  };

  // ---------- Export ----------
  const handleExport = async () => {
    setExporting(true);
    try {
      const resp = await axios.get(`${API}/export/production-orders`, { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `production_orders_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch (err) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  // ---------- Helpers ----------
  const hasActiveFilters = searchQuery || statusFilter !== "all";
  const clearFilters = () => { setSearchQuery(""); setStatusFilter("all"); };
  const uniqueStatuses = [...new Set(orders.map((o) => o.status))].sort();

  // ---------- Render ----------
  return (
    <div data-testid="po-tracker-page">
      {/* Header */}
      <DataSourceNotice source={dataSource} message={sourceMessage} />
      <div className="page-header">
        <div>
          <h1 className="page-title heading-display" data-testid="po-tracker-title">Production Order Tracker</h1>
          <p className="text-muted-foreground mt-1 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Live data from SAP S/4HANA &middot; {orders.length} orders
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="btn-industrial" onClick={handleExport} disabled={exporting || loading} data-testid="export-excel-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export Excel
          </Button>
          <Button variant="outline" className="btn-industrial" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card" data-testid="kpi-total">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="stat-card-value">{kpis.total}</p>
              <p className="stat-card-label">Total Orders</p>
            </div>
          </div>
        </div>
        <div className="stat-card" data-testid="kpi-released">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-blue-500/10 flex items-center justify-center">
              <PlayCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="stat-card-value">{kpis.released}</p>
              <p className="stat-card-label">Released</p>
            </div>
          </div>
        </div>
        <div className="stat-card" data-testid="kpi-in-progress">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-amber-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="stat-card-value">{kpis.in_progress}</p>
              <p className="stat-card-label">In Progress</p>
            </div>
          </div>
        </div>
        <div className="stat-card" data-testid="kpi-completed">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="stat-card-value">{kpis.completed}</p>
              <p className="stat-card-label">Completed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar" data-testid="po-filter-bar">
        <div className="search-input-wrapper flex-1 min-w-[200px] max-w-md">
          <Search />
          <Input
            placeholder="Search PO number, material, sales order..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="po-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="po-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {uniqueStatuses.map((s) => (
              <SelectItem key={s} value={s}>{s} ({statusCounts[s] || 0})</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="po-clear-filters">
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}

        {selected.size > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <Badge variant="secondary">{selected.size} selected</Badge>
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleBatchRelease}
              disabled={batchReleasing}
              data-testid="batch-release-btn"
            >
              {batchReleasing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
              Release Selected
            </Button>
          </div>
        )}

        {hasActiveFilters && !selected.size && (
          <Badge variant="secondary" className="ml-auto">{filtered.length} results</Badge>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 rounded-md" />)}
        </div>
      ) : sorted.length === 0 ? (
        <div className="empty-state" data-testid="po-empty-state">
          <Package className="empty-state-icon" />
          <h3 className="text-lg font-medium mb-2">No Production Orders Found</h3>
          <p className="text-muted-foreground mb-4">
            {hasActiveFilters ? "Try adjusting your filters" : "No production orders in the system yet"}
          </p>
          {hasActiveFilters && <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>}
        </div>
      ) : (
        <Card className="card-industrial" data-testid="po-tracker-table">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="w-10">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <Checkbox
                              checked={allReleasableSelected}
                              onCheckedChange={toggleSelectAll}
                              disabled={releasable.length === 0}
                              data-testid="select-all-checkbox"
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Select all &apos;Created&apos; orders for release</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  <TableHead className="font-semibold cursor-pointer select-none" onClick={() => toggleSort("po_number")} data-testid="sort-po-number">
                    <div className="flex items-center">PO Number <SortIcon field="po_number" /></div>
                  </TableHead>
                  <TableHead className="font-semibold cursor-pointer select-none" onClick={() => toggleSort("material")}>
                    <div className="flex items-center">Material <SortIcon field="material" /></div>
                  </TableHead>
                  <TableHead className="font-semibold cursor-pointer select-none text-right" onClick={() => toggleSort("quantity")}>
                    <div className="flex items-center justify-end">Quantity <SortIcon field="quantity" /></div>
                  </TableHead>
                  <TableHead className="font-semibold cursor-pointer select-none" onClick={() => toggleSort("status")}>
                    <div className="flex items-center">Status <SortIcon field="status" /></div>
                  </TableHead>
                  <TableHead className="font-semibold">
                    <div className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Planned Start</div>
                  </TableHead>
                  <TableHead className="font-semibold">
                    <div className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Planned End</div>
                  </TableHead>
                  <TableHead className="font-semibold">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="flex items-center gap-1">
                          <Clock className="w-4 h-4" /> Committed
                        </TooltipTrigger>
                        <TooltipContent>Committed delivery date from order schedule</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  <TableHead className="font-semibold">Sales Order</TableHead>
                  <TableHead className="font-semibold">
                    <div className="flex items-center gap-1"><Factory className="w-4 h-4" /> Plant</div>
                  </TableHead>
                  <TableHead className="font-semibold text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((order) => {
                  const isCreated = order.status === "Created";
                  const isReleasing = releasingSet.has(order.po_number);
                  return (
                    <TableRow
                      key={order.po_number}
                      className="hover:bg-secondary/30 transition-colors"
                      data-testid={`po-row-${order.po_number}`}
                    >
                      <TableCell>
                        {isCreated && (
                          <Checkbox
                            checked={selected.has(order.po_number)}
                            onCheckedChange={() => toggleSelect(order.po_number)}
                            data-testid={`select-po-${order.po_number}`}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="data-value font-medium" data-testid={`po-number-${order.po_number}`}>
                          {order.po_number}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium">{order.material}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="data-value font-medium">{order.quantity.toLocaleString()}</span>
                        <span className="text-muted-foreground text-xs ml-1">{order.unit}</span>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={order.status} />
                      </TableCell>
                      <TableCell>
                        <span className="data-value text-sm">{formatDate(order.planned_start)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="data-value text-sm">{formatDate(order.planned_end)}</span>
                      </TableCell>
                      <TableCell>
                        {order.committed_date ? (
                          <span className="data-value text-emerald-600 text-sm">{formatDate(order.committed_date)}</span>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {order.sales_order ? (
                          <div>
                            <span className="data-value text-sm">{order.sales_order}</span>
                            {order.sales_order_item && (
                              <span className="text-muted-foreground text-xs ml-1">/ {order.sales_order_item}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="data-value">{order.plant || "-"}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {isCreated ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-3 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                            onClick={() => handleReleaseSingle(order.po_number)}
                            disabled={isReleasing}
                            data-testid={`release-btn-${order.po_number}`}
                          >
                            {isReleasing ? <Loader2 className="w-3 h-3 animate-spin" /> : "Release"}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductionOrderTracker;
